import { type DropdownFilterTranslation } from '@/components/filtering/filter-set/types'

import {
  type PayoutKeys,
  type TransactionKeys,
} from '../components/payouts/payout-export/payoutExportUtils'
import {
  type AllSubscriptionTypes,
  type LocationSubscriptionStatus,
  type LocationSubscriptionTier,
  type PayoutReconciliationStatus,
  type PayoutStatus,
  type PayoutTransactionSubjectType,
  type PayoutTransactionType,
  type PayoutType,
  type TransactionV2,
} from '../types'

export interface BillingEn {
  payout: {
    export: {
      payoutHeaders: Record<PayoutKeys, string>
      transactionHeaders: Record<TransactionKeys, string>
      sections: Record<string, string>
    }
    type: Record<PayoutType, string>
    status: Record<PayoutStatus, string>
    reconciliationStatus: Record<PayoutReconciliationStatus, string>
    transaction: {
      type: Record<PayoutTransactionType, string>
      subject: Record<PayoutTransactionSubjectType, string>
    }
  }
  subscription: {
    tier: Record<LocationSubscriptionTier, string>
    type: Record<AllSubscriptionTypes, string>
    status: Record<LocationSubscriptionStatus | 'missing', string>
  }
  transactions: {
    filters: Record<
      'status' | 'paymentMethod' | 'type',
      DropdownFilterTranslation | string
    >
  }
}

// Card filter option will cover both card and card-present
type PaymentMethod = Exclude<
  NonNullable<TransactionV2['paymentMethod']>,
  'card-present'
>

// Dispute filter option will cover both dispute and dispute-reversal
type TransactionType = Exclude<TransactionV2['type'], 'dispute-reversal'>

const transactionTypeOptions: Record<TransactionType, string> = {
  charge: 'Charge',
  dispute: 'Dispute',
  refund: 'Refund',
}

const paymentMethodOptions: Record<PaymentMethod, string> = {
  card: 'Card',
  cash: 'Cash',
  'third-party': 'Third Party',
}

const statusOptions: Record<TransactionV2['status'], string> = {
  canceled: 'Canceled',
  failed: 'Failed',
  pending: 'Pending',
  succeeded: 'Paid',
}

export const billingEn: BillingEn = {
  payout: {
    export: {
      payoutHeaders: {
        adjustments: 'Adjustments',
        arrivalDate: 'Arrival Date',
        chargebacks: 'Chargebacks',
        delinquencyFees: 'Delinquency Fees',
        deliveryFeesCollected: 'Delivery Fees Collected',
        deliveryFeesDistributed: 'Delivery Fees Distributed',
        discounts: 'Discounts',
        giftCard: 'Gift Card Redemptions',
        grossRevenue: 'Gross Revenue',
        inHouseTips: 'In-House Tips',
        initiatedDate: 'Initiated Date',
        locations: 'Locations',
        netPayout: 'Total',
        netRevenue: 'Net Revenue',
        otherRevenueFees: 'Other Fees',
        otherRevenueTotal: 'Other Revenue Total',
        ownerFees: 'Owner Fees',
        ownerFeesCollected: 'Owner Fees Collected',
        processingFeesCollected: 'Processing Fees Collected',
        processingFeesDistributed: 'Processing Fees Distributed',
        reconciliationStatus: 'Reconciliation Status',
        subtotal: 'Subtotal',
        taxes: 'Taxes',
        thirdPartyTipsCollected: 'Third Party Tips Collected',
        thirdPartyTipsDistributed: 'Third Party Tips Distributed',
        waived: 'Waived Fees',
      },
      sections: {
        payout: 'Payout',
        'payout-not-reconciled':
          'This payout is still being reconciled.  Check back later for more complete details.',
        transactions: 'Transactions',
      },
      transactionHeaders: {
        adjustments: 'Adjustments',
        chargebacks: 'Chargebacks',
        delinquencyFees: 'Delinquency Fees',
        deliveryFeesCollected: 'Delivery Fees Collected',
        deliveryFeesDistributed: 'Delivery Fees Distributed',
        discounts: 'Discounts',
        giftCard: 'Gift Card Redemptions',
        grossRevenue: 'Gross Revenue',
        inHouseTips: 'In-House Tips',
        location: 'Location',
        netPayout: 'Total',
        netRevenue: 'Net Revenue',
        orderType: 'Order Type',
        otherRevenueFees: 'Other Fees',
        otherRevenueTotal: 'Other Revenue Total',
        ownerFees: 'Owner Fees',
        ownerFeesCollected: 'Owner Fees Collected',
        processingFeesCollected: 'Processing Fees Collected',
        processingFeesDistributed: 'Processing Fees Distributed',
        subjectId: 'Source ID',
        subjectType: 'Source',
        subtotal: 'Subtotal',
        taxes: 'Taxes',
        thirdPartyTipsCollected: 'Third Party Tips Collected',
        thirdPartyTipsDistributed: 'Third Party Tips Distributed',
        transactionDate: 'Transaction Date',
        type: 'Type',
        waived: 'Waived Fees',
      },
    },
    reconciliationStatus: {
      complete: 'Complete',
      failed: 'Unreconcilable',
      not_applicable: 'Not Applicable',
      pending: 'Pending',
    },
    status: {
      canceled: 'Canceled',
      failed: 'Failed',
      in_transit: 'In Transit',
      paid: 'Paid',
      pending: 'Pending',
    },
    transaction: {
      subject: {
        external: 'External',
        'gift-card': 'Gift Card',
        order: 'Order',
      },
      type: {
        adjustment: 'Adjustment',
        advance: 'Advance',
        'advance-funding': 'Advance Funding',
        'anticipation-repayment': 'Anticipation Repayment',
        'application-fee': 'Application Fee',
        'application-fee-refund': 'Application Fee Refund',
        'balance-transfer-inbound': 'Balance Transfer Inbound',
        'balance-transfer-outbound': 'Balance Transfer Outbound',
        charge: 'Charge',
        'climate-order-purchase': 'Climate Order Purchase',
        'climate-order-refund': 'Climate Order Refund',
        'connect-collection-transfer': 'Connect Collection Transfer',
        contribution: 'Contribution',
        dispute: 'Dispute',
        'dispute-won': 'Dispute Won',
        'financing-paydown': 'Financing Paydown',
        'financing-paydown-reversal': 'Financing Paydown Reversal',
        'financing-payout': 'Financing Payout',
        'financing-payout-reversal': 'Financing Payout Reversal',
        'issuing-authorization-hold': 'Issuing Authorization Hold',
        'issuing-authorization-release': 'Issuing Authorization Release',
        'issuing-dispute': 'Issuing Dispute',
        'issuing-transaction': 'Issuing Transaction',
        'obligation-outbound': 'Obligation Outbound',
        'obligation-reversal-inbound': 'Obligation Reversal Inbound',
        payment: 'Payment',
        'payment-failure-refund': 'Payment Failure Refund',
        'payment-network-reserve-hold': 'Payment Network Reserve Hold',
        'payment-network-reserve-release': 'Payment Network Reserve Release',
        'payment-refund': 'Payment Refund',
        'payment-reversal': 'Payment Reversal',
        'payment-unreconciled': 'Unreconciled Payment',
        payout: 'Payout',
        'payout-cancel': 'Payout Cancel',
        'payout-failure': 'Payout Failure',
        'payout-minimum-balance-hold': 'Payout Minimum Balance Hold',
        'payout-minimum-balance-release': 'Payout Minimum Balance Release',
        'platform-credit': 'Platform Credit',
        refund: 'Refund',
        'refund-failure': 'Refund Failure',
        'reserve-transaction': 'Reserve Transaction',
        'reserved-funds': 'Reserved Funds',
        'stripe-balance-payment-debit': 'Stripe Balance Payment Debit',
        'stripe-balance-payment-debit-reversal':
          'Stripe Balance Payment Debit Reversal',
        'stripe-fee': 'Stripe Fee',
        'stripe-fx-fee': 'Stripe FX Fee',
        'tax-fee': 'Tax Fee',
        topup: 'Topup',
        'topup-reversal': 'Topup Reversal',
        transfer: 'Transfer',
        'transfer-cancel': 'Transfer Cancel',
        'transfer-failure': 'Transfer Failure',
        'transfer-refund': 'Transfer Refund',
        unknown: 'Unknown',
      },
    },
    type: {
      automatic: 'Automatic',
      instant: 'Instant',
    },
  },
  subscription: {
    status: {
      active: 'Active',
      cancelled: 'Canceled',
      delinquent: 'Delinquent',
      missing: 'None',
      past_due: 'Past due',
      paused: 'Paused',
      trialing: 'Trialing',
    },
    tier: {
      'growth-demo': 'Growth demo',
      none: 'None',
      standard: 'Standard',
      website: 'Website',
    },
    type: {
      default: 'Default',
      'flex-primary': 'Flex primary',
      'flex-secondary': 'Flex secondary',
      'flex-single': 'Flex single',
      legacy: 'Legacy',
      primary: 'Primary',
      secondary: 'Secondary',
      single: 'Single',
      'super-flex-single': 'Super flex single',
    },
  },
  transactions: {
    filters: {
      paymentMethod: {
        label: 'Payment Method',
        options: paymentMethodOptions,
      },
      status: {
        label: 'Status',
        options: statusOptions,
      },
      type: {
        label: 'Type',
        options: transactionTypeOptions,
      },
    },
  },
}
